import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import "../styles/patient.scss";
import TopBar from "components/common/TopBar";
import EventService from "service/event/EventService";
import AppointmentService from "modules/appointments/service/AppointmentService";
import {
  isEndStatus,
  opensCallNote,
  rescheduleBlockReason,
  SERVICE_TYPE,
  STATUS_LABEL,
} from "modules/appointments/helper/appointmentConstants";
import { formatTimeSlot } from "modules/appointments/helper/formatters";
import PatientService from "../service/PatientService";
import { normalizeLanguages } from "../helper/patientFormatters";
import { toTouchpoints } from "../helper/touchpoints";
import PatientHeaderCard from "./PatientHeaderCard";
import useRescheduleFlow from "./useRescheduleFlow";
import PatientShell from "./PatientShell";
import { withoutUnsavedCardsGuard } from "./UnsavedCardsGuard";
import QuoteBanner from "./QuoteBanner";
import HealthOverviewCard from "./HealthOverviewCard";
import AnthroOverviewCard from "./AnthroOverviewCard";
import useCallFlow from "./useCallFlow";
import { useOriginalBooking } from "./useOriginalBookingName";
import { bookingForErrorText, overrideMemberId } from "../helper/bookingFor";
import SECTIONS from "../sections";
import { SECTION_SELECT } from "../sections/railEvents";
import { PD_V2_REFRESH } from "../sections/patientDetails/events";
import TherapyService from "../service/TherapyService";
import useStatusChange from "modules/appointments/hooks/useStatusChange";
import UserService from "modules/user/service/UserService";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobilePatientScreen from "mobile/screens/MobilePatientScreen";

/**
 * Patient details, reached by clicking a row on the appointments dashboard.
 *
 * Keyed on :appointmentId, not :patientId — it is the only id that retrieves
 * both halves: the appointment carries the patientID everything else fans out
 * from, and a patient id alone can't say which appointment the strip describes.
 *
 * Scope: top bar + header card. Everything below is deliberately absent rather
 * than stubbed into the DOM.
 */
/**
 * `?section=diagnosis` opens that tab straight away — how Create Rx's
 * "+ Add diagnosis" lands the doctor in the right place.
 *
 * Validated against the REGISTRY, not the sidebar's list: `ai-transcript` is a
 * sidebar key with no registry entry (it opens a modal), and any unknown key
 * would otherwise render the shell's "being built" placeholder under a
 * highlighted tab — a convincing-looking empty section.
 */
const sectionFromUrl = (search) => {
  try {
    const want = new URLSearchParams(search || "").get("section");
    return want && SECTIONS[want] ? want : null;
  } catch (e) {
    return null;
  }
};

/**
 * `?section=<key>&add=1` — Create Rx's "+ Add …": land on that section AND open
 * its add form (sections/common/useAutoOpenAdd). The section key, or null.
 */
const addFromUrl = (search) => {
  try {
    return new URLSearchParams(search || "").get("add") === "1" ? sectionFromUrl(search) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Bring #ph-sections to its scroll-margin, and KEEP it there while the page above
 * it is still growing.
 *
 * WHY NOT ONE scrollIntoView. The overview row above the sections (risk
 * narrative, quick-glimpse chips, anthropometric and lab tiles) fills in from its
 * own requests AFTER the screen is ready, and grows by hundreds of pixels. This
 * page opts out of scroll anchoring (overflow-anchor: none — see patient.scss), so
 * nothing kept a one-shot scroll's target in place: "+ Add lab investigation" from
 * Create Rx landed wherever the page happened to be tall enough at that instant,
 * with the overview still filling the screen.
 *
 * So after the first scroll, every size change of the page, the header or the
 * sections re-aligns — until the doctor wheels, taps, clicks or presses a key that scrolls, or the page
 * is moved by anyone else's hand, or HOLD_IDLE_MS pass with nothing changing size.
 *
 * TELLING OUR SCROLLS FROM ANYONE ELSE'S (the part the first version got wrong).
 * A scroll event arrives a frame after the scrollTo that caused it, and while the
 * header collapses (the first scroll takes the page past COLLAPSE_ENTER) the target
 * moves every frame. The first version re-measured the target when the event came
 * in, so after one slow frame its OWN correction read as "moved away" and it
 * cancelled itself 50–500ms in — measured: 6 of 8 in-app runs at 1512x860 with the
 * overview landing fast, 21 of 21 with one 150ms long task during the collapse — and
 * the overview then pushed the section off screen. So a scroll event is judged
 * against where OUR last scroll put the page, never against a fresh measurement:
 *   · it is where we left the page                    -> ours
 *   · something resized since our last scroll         -> undecided; the re-align
 *                                                        that resize queued re-syncs
 *   · it is the page's new bottom, above where we were -> the page got shorter and
 *                                                        the browser clamped us
 *   · it lies between where we were and where we aimed -> our smooth scroll, or an
 *                                                        un-clamp
 *   · anything else                                    -> someone else's: stop.
 *
 * THE MARGIN IS THE HEADER'S LIVE HEIGHT. scroll-margin-top is
 * calc(var(--ph-sticky-h) + 16px), and --ph-sticky-h is republished by a
 * ResizeObserver a frame AFTER the header resizes — so during the collapse the CSS
 * margin is always a frame stale and every re-align aimed short. The header's own
 * box is read instead, which makes the hold scroll once instead of chasing.
 *
 * NO FIXED CAP. The first version stopped after 10s, and the overview's requests
 * take as long as the network does: a risk narrative landing at 11s (history, then
 * detail), or a request retried once by getAgain, grew the page after the hold had
 * gone. Holding costs nothing while nothing changes, and the doctor's first
 * wheel, tap, click or scrolling key still ends it — so the backstop is two minutes of the
 * visible page not changing size at all.
 *
 * Returns a cancel function.
 */
const HOLD_IDLE_MS = 120000;
const alignSections = ({ smooth } = {}) => {
  const el = document.getElementById("ph-sections");
  if (!el) return () => {};
  const root = document.documentElement;
  const sticky = document.querySelector(".ph-sticky");
  const margin = () => {
    const css = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
    if (!sticky) return css;
    const published = parseFloat(window.getComputedStyle(root).getPropertyValue("--ph-sticky-h"));
    return published > 0 ? css + sticky.getBoundingClientRect().height - published : css;
  };
  const targetTop = () =>
    Math.max(0, Math.round(el.getBoundingClientRect().top + window.pageYOffset - margin()));
  const maxY = () => root.scrollHeight - window.innerHeight;

  let expectY = window.pageYOffset; // where the page was right after our last scroll
  let wantY = expectY; // what that scroll asked for, before any clamp
  let resized = false; // something changed size since then
  const go = (behavior) => {
    const top = targetTop();
    if (Math.abs(window.pageYOffset - top) > 1) window.scrollTo({ top, behavior });
    expectY = window.pageYOffset;
    wantY = top;
    resized = false;
  };

  let done = false;
  let raf = 0;
  let idleTimer = 0;
  const observers = [];
  const onScroll = () => {
    const y = window.pageYOffset;
    if (Math.abs(y - expectY) <= 2 || resized) return;
    if (y < expectY && y >= maxY() - 2) {
      expectY = y;
      return;
    }
    if ((y - expectY) * (wantY - y) >= 0) {
      expectY = y;
      return;
    }
    /* The target itself moved since our last scroll: this is layout (scroll
       anchoring's compensation, or layout forced while the tab was hidden), not
       a hand. A foreign scroll leaves the target where it was, and still stops
       the hold. */
    if (Math.abs(targetTop() - wantY) > 2) {
      onLayout();
      return;
    }
    cancel();
  };
  /* A wheel event that moves nothing (a trackpad's zero-delta phase event) is not
     the doctor scrolling. */
  const onIntent = (e) => {
    if (e && e.type === "wheel" && !e.deltaX && !e.deltaY) return;
    /* Only a key that can move the page or focus is someone taking over — not
       Cmd+Tab, Ctrl+Tab, a bare Shift, Escape or a copy shortcut. A key that does
       move the page anyway fires a scroll event, which onScroll judges. */
    if (e && e.type === "keydown" && SCROLL_KEYS.indexOf(e.key) === -1) return;
    cancel();
  };
  const cancel = () => {
    if (done) return;
    done = true;
    window.clearTimeout(idleTimer);
    if (raf) window.cancelAnimationFrame(raf);
    observers.forEach((o) => o.disconnect());
    USER_INTENT.forEach((type) => window.removeEventListener(type, onIntent, true));
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("visibilitychange", onVisible);
  };
  /* A hidden tab does no layout, so "nothing changed size" means nothing there:
     the backstop only ends a hold the user can see. */
  const armIdle = () => {
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => (document.hidden ? armIdle() : cancel()), HOLD_IDLE_MS);
  };
  /* Coming back to the tab: re-align once from the live layout, in case what
     landed while it was hidden has not been reported yet. */
  const onVisible = () => {
    if (!document.hidden) onLayout();
  };
  const onLayout = () => {
    resized = true;
    if (done || raf) return;
    raf = window.requestAnimationFrame(() => {
      raf = 0;
      if (done) return;
      go("auto");
      armIdle();
    });
  };

  go(smooth ? "smooth" : "auto");
  armIdle();
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onVisible);
  USER_INTENT.forEach((type) => window.addEventListener(type, onIntent, { capture: true, passive: true }));
  if (typeof window.ResizeObserver === "function") {
    /* An observer reports every target once on observe(). That is not a layout
       change, and acting on it would cut a smooth first scroll short. */
    let primed = false;
    const ro = new window.ResizeObserver(() => {
      if (!primed) {
        primed = true;
        return;
      }
      onLayout();
    });
    [document.querySelector(".patient-scroll"), sticky, el]
      .filter(Boolean)
      .forEach((node) => ro.observe(node));
    observers.push(ro);
  }
  return cancel;
};
/* The doctor's own hand. A programmatic scrollTo fires none of these. */
const USER_INTENT = ["wheel", "touchstart", "keydown", "mousedown"];
const SCROLL_KEYS = ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Spacebar", "Tab"];

/** Collapse once past this, expand only after coming back under COLLAPSE_EXIT. */
const COLLAPSE_ENTER = 72;
const COLLAPSE_EXIT = 32;

const PatientDetails = ({ match, history, location }) => {
  const appointmentId = Number(match.params.appointmentId);

  const appointmentService = useRef(new AppointmentService()).current;
  const therapyService = useRef(new TherapyService()).current;

  /* WHICH primary button this header gets — at most one, never both
     (product's table, 2026-10-13):
       Doctor                                        Create Rx
       Nutritionist / Physiotherapist / Medical team Create health plan
       Psychologist (category 5, PSYCHOTHERAPIST)    neither
     A nutritionist, physiotherapist and psychologist all have a doctorProfile
     and reach this screen, so `doctorProfile.id > 0` is not the test — it would
     hand them the prescribe button. isPrescriber() fails closed on a missing
     category, so an unrecognised clinician gets the health plan rather than the
     prescription pad (UserService.canCreateHealthPlan). */
  const canPrescribe = useMemo(() => new UserService().isPrescriber(), []);
  const canPlan = useMemo(() => new UserService().canCreateHealthPlan(), []);

  /* MOBILE ONLY. Everything mobile lives under src/mobile/; this line and the
     branch at the end of render are the whole of its footprint in this file.
     Called unconditionally, as a hook must be — MOBILE.patientScreen gates the
     result, not the call. False here (either flag off, or a viewport wider than
     the breakpoint) and this file behaves exactly as it did before mobile
     existed. */
  const mobile = useIsMobile() && MOBILE.patientScreen;
  const patientService = useRef(new PatientService()).current;

  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [languages, setLanguages] = useState([]);
  /**
   * "loading" | "ok" | "error". Kept apart from the list because an EMPTY list
   * and a FAILED fetch are different facts: one says this patient has no
   * languages recorded, the other says we don't know. Rendering both as
   * "No preferred languages" made a broken fetch undiagnosable.
   */
  const [languagesState, setLanguagesState] = useState("loading");
  const [reasons, setReasons] = useState([]);
  // Header condition chip + the touchpoints strip. Decoration: each fetch fails
  // silently (empty ⇒ the chip/entry just isn't drawn), never blocking the page.
  /* The therapy chip is a PICKER now, so its tag has to be kept, not just the
     display name — the menu marks the current option by tag. */
  const [condition, setCondition] = useState("");
  const [therapyTag, setTherapyTag] = useState("");
  const [pastAppts, setPastAppts] = useState([]);
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  /**
   * "loading" | "ok" | "error", for the same reason languagesState exists: an
   * empty list and an unfinished fetch are different facts. Both start as [],
   * so the strip claimed "No past appointments" / "None scheduled" for the
   * whole of the fetch — a confident answer to a question not yet asked.
   */
  const [touchpointsState, setTouchpointsState] = useState("loading");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Which section is open. Lifted here so its title can sit above the breadcrumb,
  // as in Figma; the sidebar (inside PatientShell) drives it.
  const [section, setSection] = useState(
    () => sectionFromUrl(location && location.search) || "anthropometric"
  );
  /* The section whose add form opens once on arrival, or null. Read ONCE and
     the flag stripped from the URL straight away (section= stays), so going
     back / forward to this entry, reloading, or the "Update booking for"
     remount (which copies location.search) never opens it again. `state` is
     passed through: it carries bookingForRev, part of the route key, and a
     different key would remount the page. */
  const [autoAdd, setAutoAdd] = useState(() => addFromUrl(location && location.search));
  useEffect(() => {
    let params;
    try {
      params = new URLSearchParams((location && location.search) || "");
    } catch (e) {
      return;
    }
    if (!params.has("add")) return;
    params.delete("add");
    const rest = params.toString();
    history.replace({
      pathname: location.pathname,
      search: rest ? `?${rest}` : "",
      state: location.state,
    });
    // Mount only: the flag is read once, in the initialiser above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const consumeAutoAdd = useCallback(() => setAutoAdd(null), []);
  /* Calling the patient — the header's Call + phonebook and the sidebar's
     "ECHO - AI transcript" row (which opens the call without dialling) — via the
     shared useCallFlow hook, the same one the appointment-details screen uses. */
  /* Who the appointment is booked for, while phr's booking-for mapping points
     this record at someone else: the header's "Original appointment" pill, and
     the number a call goes to (a switch deliberately does not move the call). */
  const originalBooking = useOriginalBooking(appointment);
  const originalBookingName = originalBooking.name;
  const call = useCallFlow({ patient, appointment, callingNumber: originalBooking.callingNumber });
  /* "Update booking for" is HELD — shown disabled, with the reason on hover and
     on click — on a closed appointment, and while a call is ringing or live. */
  const bookingForBlockedReason = !appointment
    ? ""
    : isEndStatus(appointment.status)
    ? `This appointment is ${STATUS_LABEL[appointment.status] || "closed"}, so who it is booked for can't be changed.`
    : call.callActive
    ? "A call is in progress. End it before changing who this appointment is booked for."
    : "";
  const bookingForBlockedRef = useRef("");
  bookingForBlockedRef.current = bookingForBlockedReason;
  const openCall = call.openCall;
  /* The transcript modal cancels a pending "+ Add …" auto-open however it was
     opened — the header's Call and phonebook reach it without handleSelect.
     With the call connected its captured sections listen for the same add
     events, so a late list would otherwise open a second form under it. */
  useEffect(() => {
    if (call.transcriptOpen) setAutoAdd(null);
  }, [call.transcriptOpen]);

  const handleSelect = useCallback((key) => {
    /* Any deliberate navigation cancels a pending auto-open — BEFORE the
       transcript branch: opening "ECHO - AI transcript" while the list is still
       loading would otherwise let the add event reach the transcript's own
       manual-form listeners too, and open two forms. */
    setAutoAdd(null);
    if (key === "ai-transcript") {
      openCall();
      return;
    }
    setSection(key);
  }, [openCall]);

  /* Cross-section links (the medicines drug-allergies bar → Allergies, the
     overview's lab "View all" → Lab investigations) emit SECTION_SELECT rather
     than threading onSelect through every section.

     They also SCROLL, which a sidebar click does not need to: those links live
     above the sections, so opening the right tab without moving leaves the
     screen looking unchanged and the link reading as a no-op. Same #ph-sections
     anchor the `?section=` deep link uses. */
  const cancelAlign = useRef(() => {});
  useEffect(() => () => cancelAlign.current(), []);
  const handleCrossLink = useCallback(
    (key) => {
      handleSelect(key);
      if (key === "ai-transcript") return;
      window.requestAnimationFrame(() => {
        cancelAlign.current();
        cancelAlign.current = alignSections({ smooth: true });
      });
    },
    [handleSelect]
  );

  useEffect(() => {
    EventService.addListener(SECTION_SELECT, handleCrossLink);
    return () => EventService.removeListener(SECTION_SELECT, handleCrossLink);
  }, [handleCrossLink]);

  const showError = useCallback((message) => {
    EventService.emit("showError", { message });
  }, []);

  // Re-pull the patient record after the Patient-details dialog saves, so the
  // header's name / gender / age reflect the edit without a full reload. The
  // dialog hands back its optimistic copy for the instant repaint; the refetch
  // then replaces it with whatever the server actually stored (that endpoint is
  // a full-record replace, so the two can legitimately differ).
  const refetchPatient = useCallback(
    (optimistic) => {
      if (optimistic) setPatient(optimistic);
      const id = (optimistic && optimistic.id) || (patient && patient.id);
      if (!id) return;
      patientService.fetchPatient(
        id,
        (p) => {
          if (p && p.id) setPatient(p);
        },
        (err) => {
          // eslint-disable-next-line no-console
          console.warn("patient record: refetch after save failed", err);
        }
      );
    },
    [patient, patientService]
  );

  // PD_V2_REFRESH — emitted by BOTH entry points that mutate patient
  // details (the header dashboard dialog + the section-rail Patient
  // details editor). Whichever side saved, this hop:
  //   • optimistically re-seeds our local `patient` state so the
  //     header line, the section table, and every downstream consumer
  //     of the `patient` prop repaint immediately, and
  //   • re-fetches from the backend so the optimistic values are
  //     replaced with the authoritative record.
  // Same effect as calling refetchPatient(fresh) directly — going
  // through the event bus lets the section form emit without
  // threading a callback through PatientShell → the Editor.
  useEffect(() => {
    const onRefresh = (fresh) => refetchPatient(fresh);
    EventService.addListener(PD_V2_REFRESH, onRefresh);
    return () => EventService.removeListener(PD_V2_REFRESH, onRefresh);
  }, [refetchPatient]);

  // Re-pull the preferred languages after the header's popover saves them, so the
  // header line reflects the new list without a full reload.
  const refetchLanguages = useCallback(() => {
    if (!patient || !patient.id) return;
    patientService.fetchPatientLanguages(
      patient.id,
      (res) => {
        setLanguages(normalizeLanguages(res));
        setLanguagesState("ok");
      },
      (err) => {
        setLanguages([]);
        setLanguagesState("error");
        // eslint-disable-next-line no-console
        console.warn("preferred languages: refetch failed", err);
      }
    );
  }, [patient, patientService]);

  /* After a reschedule, re-read the appointment so the header's date/time strip
     shows the server's new slot — bridge's onRefreshStatus: no optimistic
     merge, and a failed re-read toasts rather than failing silently. */
  const onRescheduled = useCallback(() => {
    if (!appointmentId) return;
    appointmentService.fetchAppointmentById(
      appointmentId,
      (res) => {
        const found = res && res.appointments && res.appointments[0];
        if (found) setAppointment(found);
      },
      (err) =>
        EventService.emit("showError", {
          message: `Error: ${err === null || err === undefined ? undefined : err.message}`,
        })
    );
  }, [appointmentId, appointmentService]);

  /* "Update booking for": save the mapping, then REMOUNT this screen, so every
     fetch runs again for the new member and nothing loaded for the previous one
     (languages, therapy, touchpoints, open editors, the documents cache) can
     linger and be written under the new id. The route keys on
     location.state.bookingForRev (App.js). Resolves on success; on failure it
     says why and rejects, which keeps the confirmation open. */
  const updateBookingFor = useCallback(
    (member) =>
      new Promise((resolve, reject) => {
        /* Re-checked at confirm time: the status, or a call, can change while
           the list or the confirmation is open. */
        if (bookingForBlockedRef.current) {
          EventService.emit("showError", { message: bookingForBlockedRef.current });
          reject(new Error(bookingForBlockedRef.current));
          return;
        }
        appointmentService.setBookingFor(
          appointmentId,
          member.memberPatientId,
          () => {
            resolve();
            /* Only if the doctor is still HERE. A slow save that lands after
               they have navigated away must not drag them back. Past the
               open-card guard: UpdateBookingFor asked about the open cards
               before this save, and asking again now would come too late,
               after the mapping is saved. */
            if (history.location.pathname !== location.pathname) return;
            withoutUnsavedCardsGuard(() =>
              history.replace({
                pathname: location.pathname,
                search: location.search,
                state: { bookingForRev: Date.now() },
              })
            );
          },
          (err) => {
            EventService.emit("showError", {
              message: `Couldn’t update booking for. ${bookingForErrorText(err)}`.trim(),
            });
            reject(err);
          }
        );
      }),
    [appointmentService, appointmentId, history, location]
  );

  // The header's Reschedule button — bridge's profile check, then the dialog.
  const { startReschedule, rescheduleUI } = useRescheduleFlow({ appointment, onRescheduled });

  const goToDashboard = useCallback(() => history.push("/navigo-module"), [history]);

  // Open another of this patient's appointments from a touchpoints dropdown row.
  // Same route, new :appointmentId — the page refetches for the chosen appt.
  /* The APPOINTMENT screen, not /navigo-module/patient/:id. That route is this
     one — the header card lives on it — so pushing it with a different id
     re-rendered in place and read as "the click does nothing". A touchpoint is
     an appointment, and the thing to open is its detail page. */
  const openAppointment = useCallback(
    (id) => history.push(`/navigo-module/appointment/${id}`),
    [history]
  );

  const goToPrescription = useCallback(
    () => history.push(`/navigo-module/prescription/${appointmentId}`),
    [history, appointmentId]
  );

  /* ---------- appointment, then the patient it points at ---------- */
  useEffect(() => {
    let cancelled = false;

    if (!appointmentId) {
      setError("That appointment link looks wrong.");
      setLoading(false);
      return undefined;
    }

    appointmentService.fetchAppointmentById(
      appointmentId,
      (res) => {
        if (cancelled) return;
        const found = res && res.appointments && res.appointments[0];
        if (!found) {
          setError("We couldn't find that appointment.");
          setLoading(false);
          return;
        }
        setAppointment(found);

        /* WHOSE record this screen shows. On a family booking the appointment
           is FOR `bookedFor` — the server says so itself, overwriting the
           row's patientName/patientGender/patientEmail with that member's,
           gated on `BookedFor != 0`
           (api-appointment-service repository/appointment.go:911-916). The
           booker in `patientID` is who paid, not who is being seen.

           This screen used to load `patientID` unconditionally, so opening a
           child's appointment showed the PARENT's record — with the child's
           name in the row that got you there. Same resolution as the
           appointments dashboard's Patient ID column and the appointment
           -details screen.

           Safe for the touchpoints query too: both the singular `patientId`
           and the plural `patientIds` filters are an $or over bookedFor OR
           patientID server-side (repository/appointment.go:420-435), so a
           member's id still returns every appointment that concerns them. */
        /* ...unless phr's booking-for mapping (the appointment's `updatedBookingFor`,
           "Update booking for") points it at another member — then THAT member. */
        const pid =
          overrideMemberId(found) ||
          (found.bookedFor && found.bookedFor !== 0 ? found.bookedFor : found.patientID);

        patientService.fetchPatient(
          pid,
          (p) => {
            if (cancelled) return;
            if (!p || !p.id) {
              setError("We couldn't load this patient's record.");
            } else {
              setPatient(p);
            }
            setLoading(false);
          },
          (err) => {
            if (cancelled) return;
            setError(
              `We couldn't load this patient's record. ${(err && err.message) || ""}`
            );
            setLoading(false);
          }
        );

        patientService.fetchPatientLanguages(
          pid,
          (res2) => {
            if (cancelled) return;
            setLanguages(normalizeLanguages(res2));
            setLanguagesState("ok");
          },
          // Not fatal — the page still works without it — but no longer
          // SILENT: swallowing this is what let a failed fetch masquerade as
          // "this patient has no preferred languages".
          (err) => {
            if (cancelled) return;
            setLanguages([]);
            setLanguagesState("error");
            // eslint-disable-next-line no-console
            console.warn("preferred languages: fetch failed", err);
          }
        );

        // Condition chip: read the primary-therapy code, then map it to a name
        // via the (patient-agnostic) options list. Silent on failure — no
        // therapy, or a code the list doesn't carry, just means no chip.
        /* Through TherapyService, NOT PatientService's own two readers: the
           picker in the header uses it too, and its list cache is what stops
           the patient-agnostic master list being fetched twice on every page.
           The normalising (`id` vs `tag`, int vs string) lives there as well,
           so there is one place to be wrong rather than two. */
        therapyService.list(
          (rows) => {
            if (cancelled) return;
            therapyService.current(
              pid,
              (tag) => {
                if (cancelled || !tag) return;
                const match = rows.find((r) => String(r.tag) === String(tag));
                setTherapyTag(tag);
                if (match) setCondition(match.text);
              },
              () => {}
            );
          },
          () => {}
        );

        // Touchpoints: this patient's other CONSULTATIONS, split into past and
        // upcoming. Also decoration — failure leaves the strip's empty states.
        /* Consultations only (decision 2026-09-14): phr is a consultation app,
           and the strip used to list the patient's lab tests, health checkups
           and vaccines too — each of which opened its details page with
           Reschedule live. The server filters on it (AppointmentFilters
           serviceTypeID, repository/appointment.go $in), so the 3-per-column cap
           is filled with consultations rather than trimmed after the fact. */
        appointmentService.fetchAppointments(
          {
            pageNum: 1,
            pageSize: 100,
            patientId: pid,
            statusOrderSort: true,
            serviceTypeID: [SERVICE_TYPE.CONSULTATION],
          },
          (res3) => {
            if (cancelled) return;
            const { past, upcoming } = toTouchpoints(
              (res3 && res3.appointments) || [],
              found.id,
              found.appointmentDate
            );
            setPastAppts(past);
            setUpcomingAppts(upcoming);
            setTouchpointsState("ok");
          },
          () => {
            if (cancelled) return;
            setTouchpointsState("error");
          }
        );
      },
      (err) => {
        if (cancelled) return;
        setError(`We couldn't load that appointment. ${(err && err.message) || ""}`);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [appointmentId, appointmentService, patientService]);

  /* ---------- reason master list: id -> name, fetched once ---------- */
  useEffect(() => {
    let cancelled = false;
    appointmentService.getReasonsForConsultation(
      (res) => {
        if (cancelled) return;
        setReasons(Array.isArray(res) ? res : (res && res.reasons) || []);
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [appointmentService]);

  const reasonName = (() => {
    const r = reasons.find((x) => x.id === (appointment && appointment.reasonId));
    return (r && (r.name || r.reason)) || "";
  })();

  /** The status a just-succeeded change applied, or null. Opens the note. */
  const [callNoteForStatus, setCallNoteForStatus] = useState(null);

  /* THE SHARED FLOW — the same hook the appointments dashboard uses, so a
     status change from here behaves identically: a cancel-family status asks for
     a reason, then posts with no refundRequest, leaving refunds to the server as
     the expert dashboard does (see hooks/useStatusChange). */
  const { changeStatus: runStatusChange, updatingId, dialogs } = useStatusChange({
    /* THIS screen keeps the note in its header card, where the manual "Call
       note" button also lives, so the hook must not open a second one. Every
       other screen lets the hook do it. */
    callNote: false,
    onApplied: (status, appt) => {
      setAppointment((prev) => ({ ...prev, status }));
      /* A status change that lands is the ONE moment a call outcome is known,
         so the call note opens itself here with that status shown. Only on
         success: a failed change knows nothing, and opening a note about an
         outcome that did not happen is worse than opening nothing. And not
         for the two "Not answered" outcomes, where nobody was reached (Nittin,
         2026-09-25): the header's own "Call note" button still opens it by
         hand, which is the only way a note is taken for those now. */
      if (opensCallNote(appt, status)) setCallNoteForStatus(status);
    },
    onError: showError,
  });
  const updating = updatingId != null;
  const changeStatus = (status) => runStatusChange(appointment, status);

  const crumbs = [
    { label: "My appointments", onClick: goToDashboard },
    { label: (patient && patient.firstName) ? `${patient.firstName} ${patient.lastName || ""}`.trim() : "Patient" },
  ];

  const ready = !loading && !error && patient && appointment;

  /**
   * Arriving via `?section=` scrolls the sections into view once. Without this
   * the right tab IS open but sits below the header card and overview row, so
   * the screen looks unchanged and the button that brought you here reads as a
   * no-op. Gated on `ready` because the shell only mounts with the patient.
   */
  /**
   * The header region pins to the top of the viewport on scroll. Once pinned
   * it sheds its secondary rows (chips, preferred languages, the appointment
   * and touchpoint strips) so it costs a bar rather than a card — hovering it
   * brings them back. See .ph-sticky / .is-collapsed in patient.scss.
   *
   * A plain scroll listener rather than IntersectionObserver: the only
   * question is "have we moved off the top", the answer is one number, and
   * this keeps the behaviour readable. rAF-throttled and passive so it cannot
   * make the scroll janky.
   */
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const y = window.pageYOffset;
        // HYSTERESIS, not a single threshold — collapsing shortens the header, so
        // landing on one number lets that shift carry you straight back across it.
        //
        // The dead band is NOT sufficient on its own, despite what this comment
        // used to claim: the collapse changes the header height by ~65px, more than
        // the 40px band, and the browser's scroll anchoring turns that height change
        // into an equal scrollY movement — so the flip WAS triggerable by its own
        // consequence (measured: 13 backward scroll jumps over a slow 120px scroll).
        // What actually breaks the loop is `overflow-anchor: none` on .patient-page;
        // this band is the second line of defence. Widening it is not a substitute —
        // the collapse delta depends on the patient's content and can exceed any
        // fixed number.
        setStuck((was) => (was ? y > COLLAPSE_EXIT : y > COLLAPSE_ENTER));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  /**
   * Publish the pinned header's LIVE height as `--ph-sticky-h` on the page
   * root. The three section columns are sticky scrollers now, and their
   * offset has to clear this header — but its height is not a constant: it
   * sheds rows when stuck, restores them on hover, and reflows responsively.
   * A hardcoded offset would tuck the columns under it at some sizes and
   * leave a gap at others.
   */
  const stickyRef = useRef(null);
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return undefined;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      el.ownerDocument.documentElement.style.setProperty("--ph-sticky-h", `${h}px`);
    };
    publish();
    // ResizeObserver is not in every browser this app supports; the collapse
    // toggle below re-runs the effect anyway, so falling back to that alone
    // still tracks the only change that matters.
    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stuck, ready]);

  const pendingScroll = useRef(!!sectionFromUrl(location && location.search));
  useEffect(() => {
    if (!ready || !pendingScroll.current) return;
    pendingScroll.current = false;
    /* After this commit paints, so #ph-sections exists; then held in place while
       the overview row above it loads (see alignSections). */
    window.requestAnimationFrame(() => {
      cancelAlign.current();
      cancelAlign.current = alignSections();
    });
  }, [ready]);

  /* The mobile arrangement of everything computed above. Placed after the last
     hook so hook order is identical on both paths, and it hands over state and
     callbacks only — it fetches nothing and decides nothing. The desktop tree
     below is unreachable on mobile and unchanged by it. */
  if (mobile) {
    return (
      <MobilePatientScreen
        loading={loading}
        error={error}
        ready={ready}
        patient={patient}
        appointment={appointment}
        appointmentId={appointmentId}
        languages={languages}
        languagesState={languagesState}
        onLanguagesSaved={refetchLanguages}
        condition={condition}
        therapyTag={therapyTag}
        onTherapyChanged={(tag, text) => {
          setTherapyTag(tag);
          setCondition(text);
        }}
        onPatientUpdated={refetchPatient}
        reasonName={reasonName}
        pastAppointments={pastAppts}
        upcomingAppointments={upcomingAppts}
        touchpointsState={touchpointsState}
        onOpenAppointment={openAppointment}
        onOpenAppointmentDetails={() =>
          history.push(`/navigo-module/appointment/${appointmentId}`)
        }
        callNoteForStatus={callNoteForStatus}
        onCloseCallNote={() => setCallNoteForStatus(null)}
        stuck={stuck}
        originalBookingName={originalBookingName}
        section={section}
        onSelectSection={handleSelect}
        updating={updating}
        onChangeStatus={changeStatus}
        onReschedule={startReschedule}
        rescheduleDisabledReason={appointment ? rescheduleBlockReason(appointment) : ""}
        /* The SAME callback and the same held-reason the desktop header gets —
           this screen owns the switch either way, and the remount it triggers
           (location.state.bookingForRev) is the route's, not the layout's. */
        onUpdateBookingFor={updateBookingFor}
        bookingForBlockedReason={bookingForBlockedReason}
        canPrescribe={canPrescribe}
        onCreatePrescription={goToPrescription}
        onCreateHealthPlan={
          canPlan ? () => history.push(`/navigo-module/health-plan/${appointmentId}`) : undefined
        }
        onBack={goToDashboard}
        call={call}
        rescheduleUI={rescheduleUI}
        dialogs={dialogs}
        locationSearch={location && location.search}
        autoOpenAdd={autoAdd}
        onAutoOpenAdd={consumeAutoAdd}
      />
    );
  }

  return (
    <div className="patient-page">
      <div className="patient-scroll">
        {/* The screen starts at the navbar. Figma's "Section · NN / <title> /
            <blurb>" block above each board is an annotation naming the board,
            not screen content — don't render it. */}
        <div ref={stickyRef} className={`ph-sticky${stuck ? " is-stuck" : ""}`}>
          <TopBar crumbs={crumbs} onBack={goToDashboard} />

          {ready && (
            <PatientHeaderCard
              patient={patient}
              languages={languages}
              languagesState={languagesState}
              appointment={appointment}
              reasonName={reasonName}
              updating={updating}
              onChangeStatus={changeStatus}
              callNoteForStatus={callNoteForStatus}
              onCloseCallNote={() => setCallNoteForStatus(null)}
              onPrimaryAction={canPrescribe ? goToPrescription : undefined}
              condition={condition}
              therapyTag={therapyTag}
              onTherapyChanged={(tag, text) => { setTherapyTag(tag); setCondition(text); }}
              pastAppointments={pastAppts}
              upcomingAppointments={upcomingAppts}
              touchpointsState={touchpointsState}
              onLanguagesSaved={refetchLanguages}
              onPatientUpdated={refetchPatient}
              onReschedule={startReschedule}
              rescheduleDisabledReason={rescheduleBlockReason(appointment)}
              onOpenAppointment={openAppointment}
              onOpenAppointmentDetails={() =>
                history.push(`/navigo-module/appointment/${appointmentId}`)
              }
              onCreateHealthPlan={
                canPlan ? () => history.push(`/navigo-module/health-plan/${appointmentId}`) : undefined
              }
              call={call}
              onUpdateBookingFor={updateBookingFor}
              bookingForBlockedReason={bookingForBlockedReason}
              originalBookingName={originalBookingName}
              collapsed={stuck}
            />
          )}
        </div>

        {loading && <div className="patient-message">Loading…</div>}

        {!loading && error && (
          <div className="patient-message is-error">
            <p>{error}</p>
            <button type="button" className="ph-btn ph-btn-primary" onClick={goToDashboard}>
              Back to appointments
            </button>
          </div>
        )}

        {ready && (
          <>

            {/* The overview row — Figma 2428:4702: a quote strip, then two 728
                cards (health overview | anthropometric overview), then the
                section shell below. */}
            <QuoteBanner />
            <div className="po-row">
              {/* Keyed by patient: on in-place appointment switches the risk
                  card must remount, not paint patient A's risk for a frame
                  under patient B's name. */}
              <HealthOverviewCard key={patient.id} patient={patient} />
              <AnthroOverviewCard patient={patient} />
            </div>

            <PatientShell
              active={section}
              onSelect={handleSelect}
              patient={patient}
              appointmentId={appointmentId}
              autoOpenAdd={autoAdd}
              onAutoOpenAdd={consumeAutoAdd}
            />
          </>
        )}
      </div>

      {/* Per-screen call surfaces from useCallFlow. The transcript modal
          itself is mounted globally by ActiveCallProvider (app root), so
          it isn't listed here anymore — closing the dialog no longer
          ends the call, and the CM can navigate away without dropping
          it. dialogsUI carries the SparkTG confirm dialog + the "no
          appointment / call failed" error dialog — both portalled, so
          they escape the header's sticky stacking context. */}
      {ready && call.dialogsUI}

      {ready && rescheduleUI}
      {/* The shared cancel dialog. Rendered here so this screen runs the
          same sequence as the dashboard rather than refusing the status. */}
      {dialogs}
    </div>
  );
};

PatientDetails.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  /** Supplied by Route — carries the `?section=` deep link. */
  location: PropTypes.object,
};

export default PatientDetails;
